import React, { type ComponentProps, useCallback, useMemo } from 'react';
import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import { isValid } from 'date-fns';
import {
  GiftCardOrderCard,
  TextSpace,
  theme,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardOrder = (props: GiftCardOrderProps) => {
  const {
    id,
    recipientName,
    deliveryMethods,
    balance = 0,
    purchaseDate,
    getConfirmationLink,
    onPress,
  } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const balanceInDollars = balance / 100;
  const formattedDate = new Date(purchaseDate);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const confirmationLink = useMemo(() => {
    return getConfirmationLink(id);
  }, [id, getConfirmationLink]);

  const handlePress = useCallback(() => {
    onPress?.(id);
  }, [id, onPress]);

  const a11yLabel = formatMessage(messages.pressableA11yLabel, { id });

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <View>
        {onPress ? (
          <GiftCardOrderPressable
            accessibilityLabel={a11yLabel}
            onPress={handlePress}
          />
        ) : (
          <GiftCardOrderCard.Link
            to={confirmationLink}
            accessibilityLabel={a11yLabel}
          />
        )}

        <GiftCardOrderCard.Container>
          <GiftCardOrderCard.Icon />

          <GiftCardOrderCard.ContentContainer>
            <GiftCardOrderCard.Text isBold>
              <FormattedMessage {...messages.title} />
            </GiftCardOrderCard.Text>

            <GiftCardOrderCard.Text>
              <FormattedMessage
                {...messages.recipientNameWithPrefix}
                values={{ recipient: recipientName }}
              />
            </GiftCardOrderCard.Text>

            {deliveryMethods.includes(DELIVERY_METHOD_MESSAGE) ? (
              <GiftCardOrderCard.Text>
                <FormattedMessage {...messages.viaMessage} />
              </GiftCardOrderCard.Text>
            ) : null}

            {deliveryMethods.includes(DELIVERY_METHOD_EMAIL) ? (
              <GiftCardOrderCard.Text>
                <FormattedMessage {...messages.viaEmail} />
              </GiftCardOrderCard.Text>
            ) : null}
          </GiftCardOrderCard.ContentContainer>

          <GiftCardOrderCard.ExtraDetailsContainer>
            <GiftCardOrderCard.Text isBold>
              <FormattedNumber
                value={balanceInDollars}
                currency="USD"
                maximumFractionDigits={2}
                // eslint-disable-next-line react/style-prop-object
                style="currency"
              />
            </GiftCardOrderCard.Text>

            <GiftCardOrderCard.Text>{id}</GiftCardOrderCard.Text>

            {isValid(formattedDate) ? (
              <GiftCardOrderCard.Text>
                <FormattedDate
                  value={formattedDate}
                  year="numeric"
                  month="2-digit"
                  day="2-digit"
                />
              </GiftCardOrderCard.Text>
            ) : null}
          </GiftCardOrderCard.ExtraDetailsContainer>
        </GiftCardOrderCard.Container>
      </View>
    );
  }

  return (
    <View>
      {onPress ? (
        <GiftCardOrderPressable
          accessibilityLabel={a11yLabel}
          onPress={handlePress}
        />
      ) : (
        <GiftCardOrderCard.Link
          to={confirmationLink}
          accessibilityLabel={a11yLabel}
        />
      )}

      <GiftCardOrderCard.Container>
        <GiftCardOrderCard.ContentContainer style={styles.contentContainerSM}>
          {isValid(formattedDate) ? (
            <GiftCardOrderCard.Text>
              <FormattedDate
                value={formattedDate}
                year="numeric"
                month="long"
                day="2-digit"
              />
            </GiftCardOrderCard.Text>
          ) : null}

          <GiftCardOrderCard.Text>{id}</GiftCardOrderCard.Text>

          <GiftCardOrderCard.Text>
            <FormattedMessage
              {...messages.recipientNameWithPrefix}
              values={{ recipient: recipientName }}
            />

            <TextSpace />

            {deliveryMethods.includes(DELIVERY_METHOD_MESSAGE) ? (
              <FormattedMessage {...messages.viaMessageLowercase} />
            ) : null}

            {deliveryMethods.includes(DELIVERY_METHOD_EMAIL) ? (
              <FormattedMessage {...messages.viaEmailLowercase} />
            ) : null}
          </GiftCardOrderCard.Text>
        </GiftCardOrderCard.ContentContainer>

        <GiftCardOrderCard.ExtraDetailsContainer
          style={styles.extraContentContainerSM}
        >
          <GiftCardOrderCard.Text>
            <FormattedNumber
              value={balanceInDollars}
              currency="USD"
              maximumFractionDigits={2}
              // eslint-disable-next-line react/style-prop-object
              style="currency"
            />
          </GiftCardOrderCard.Text>
        </GiftCardOrderCard.ExtraDetailsContainer>
      </GiftCardOrderCard.Container>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Gift card order',
    description: 'Gift card order | Title',
  },
  recipientNameWithPrefix: {
    defaultMessage: 'To {recipient}',
    description: 'Gift card order | Recipient name with prefix',
  },
  viaMessage: {
    defaultMessage: 'Via message',
    description: 'Gift card order | Via message',
  },
  viaEmail: {
    defaultMessage: 'Via email',
    description: 'Gift card order | Via message',
  },
  viaMessageLowercase: {
    defaultMessage: 'via message',
    description: 'Gift card order | Via message',
  },
  viaEmailLowercase: {
    defaultMessage: 'via email',
    description: 'Gift card order | Via message',
  },
  pressableA11yLabel: {
    defaultMessage:
      'Navigate to the gift card confirmation screen (order ID: {id})',
    description:
      'Gift card order | Navigate to the gift card confirmation screen',
  },
});

// ─── Components ──────────────────────────────────────────────────────────────

const GiftCardOrderPressable = (params: GiftCardOrderPressableProps) => {
  const { accessibilityLabel, onPress } = params;

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="link"
      style={[styles.pressable, pressableWebStyles]}
      onPress={onPress}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainerSM: {
    gap: theme.spacing['1'],
  },
  extraContentContainerSM: {
    justifyContent: 'center',
  },
  pressable: {
    ...StyleSheet.absoluteFillObject,
    margin: -theme.spacing['2'],
    borderRadius: theme.radius.medium,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DELIVERY_METHOD_MESSAGE = 'MESSAGE';
const DELIVERY_METHOD_EMAIL = 'EMAIL';

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardOrderProps = {
  id: string;
  recipientName: string;
  deliveryMethods: ReadonlyArray<string>;
  balance: number; // in cents
  purchaseDate: string; // ISO date
  getConfirmationLink: (
    giftCardOrderId: string,
  ) => ComponentProps<typeof GiftCardOrderCard.Link>['to'];
  onPress?: (giftCardOrderId: string) => void;
};

type GiftCardOrderPressableProps = {
  accessibilityLabel: string;
  onPress: () => void;
};
